<template>
  <div class="container px-0 mb-5">
    <div class="plan-structure-header" v-if="planStructure">
      <div class="d-flex align-items-center justify-content-between px-3 py-3">
        <div>
          <span class="text-muted text-uppercase small font-weight-bold">{{ levelLabel }}</span>
          <h4 class="mb-0 mt-1">{{ planStructure.name }}</h4>
        </div>
        <div class="d-flex align-items-center" style="gap: 0.5rem;">
          <router-link
            v-if="strategicPlanId"
            :to="{ name: 'strategic_plans_show', params: { id: strategicPlanId } }"
            class="btn btn-outline-secondary btn-sm"
          >
            <font-awesome-icon :icon="['fas', 'arrow-left']" class="mr-1" />
            Voltar à árvore
          </router-link>
        </div>
      </div>
    </div>

    <div v-if="loading" class="p-5 text-center text-muted">
      Carregando...
    </div>

    <b-card
      class="border-0 radius-top-0 mb-5 min-height"
      no-body
      v-else-if="planStructure"
    >
      <b-tabs
        v-model="activeRouteTab"
        pills
        lazy
        align="center"
        nav-class="pill-tabs mx-auto"
        nav-wrapper-class="d-flex"
      >
        <template #tabs-end>
          <div class="position-absolute right-button mr-4">
            <b-button
              v-show="showDraggableButton"
              pill
              variant="outline-secondary"
              size="sm"
              v-b-tooltip.hover
              title="Reordenar listagem"
              class="border-0"
              @click="$bvModal.show('tree-item-draggable-measures')"
            >
              <b-icon icon="gear"></b-icon>
            </b-button>
            <b-button
              v-show="activeRouteTab === 2"
              role="presentation"
              variant="primary"
              class="ml-2 center-button"
              @click="addActionPlan"
              pill
            >
              Nova ação
            </b-button>
          </div>
        </template>
        <b-tab title="Metas" title-link-class="rounded-pill px-5">
          <list-goals :objective-id="planStructureId" :plan-structure-id="planStructureId" />
        </b-tab>
        <b-tab title="Indicadores" title-link-class="rounded-pill px-5">
          <list-indicators :objective-id="planStructureId" :plan-structure-id="planStructureId" />
        </b-tab>
        <b-tab
          title="Ações"
          title-link-class="rounded-pill px-5"
          class="px-3"
        >
          <table-action-plans
            v-if="activeRouteTab === 2"
            class="my-4"
            :plan-structure-id="planStructureId"
            @add-action="addActionPlan"
          />
        </b-tab>
      </b-tabs>
    </b-card>

    <modal-draggable
      v-if="planStructure && planStructure.measures"
      title="Ordernar Metas"
      :items="planStructure.measures || []"
      id="tree-item-draggable-measures"
      @order="orderMeasuresById($event, planStructureId)"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import services from "@/http";

import ListGoals from "@/modules/objectives/components/ListGoals.vue";
import ListIndicators from "@/modules/objectives/components/ListIndicators.vue";
import TableActionPlans from "@/modules/action_plans/components/TableActionPlans.vue";
import ModalDraggable from "@/components/modal/ModalDraggable";
import { LocalStorage } from "@/helpers/functions/localStorage";

export default {
  components: {
    TableActionPlans,
    ModalDraggable,
    ListGoals,
    ListIndicators,
  },
  data: () => ({
    loading: false,
    planStructure: null,
    activeTab: LocalStorage("@pam/active-tab-objective").getItem || 0,
  }),
  computed: {
    planStructureId() {
      return this.$route.params.id;
    },
    strategicPlanId() {
      return this.planStructure?.strategic_plan_id || null;
    },
    levelLabel() {
      return 'Nível do Plano';
    },
    showDraggableButton() {
      return (
        this.activeRouteTab === 0 &&
        this.planStructure?.measures &&
        this.planStructure.measures.length > 1
      );
    },
    activeRouteTab: {
      get() {
        return this.activeTab;
      },
      set(value) {
        this.activeTab = value;
        LocalStorage("@pam/active-tab-objective").store(value);
      },
    },
  },
  methods: {
    ...mapActions("objectives", [
      "ActionOrderMeasuresById",
    ]),
    addActionPlan() {
      this.$router.push({
        name: "create_action_plan",
        query: { planStructureId: this.planStructureId },
      });
    },
    async orderMeasuresById($event, id) {
      try {
        const payload = { id, measures: $event };
        await this.ActionOrderMeasuresById(payload);
        this.$notifications.addNotification({
          message: "Ordem alterada com sucesso",
          type: "success",
        });
      } catch (error) {
        this.$notifications.addNotification({
          message: "Erro ao salvar",
          icon: "exclamation-triangle",
          type: "danger",
        });
      }
    },
    async loadData() {
      this.loading = true;
      try {
        const res = await services.strategic_plans.plan_structure_detail({ id: this.planStructureId });
        this.planStructure = res.data;
      } catch (e) {
        this.$notifications.addNotification({
          message: "Erro ao carregar dados do nível.",
          type: "danger",
        });
      } finally {
        this.loading = false;
      }
    },
  },
  async created() {
    await this.loadData();
  },
};
</script>

<style lang="scss" scoped>
.plan-structure-header {
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
}

::v-deep .text-order {
  color: #000;
  font-weight: normal;
  cursor: pointer;
  display: block;
  max-width: 60px;
  margin-left: auto;
  border: 1px solid transparent;
}

::v-deep .text-order:hover {
  color: #335080;
  font-weight: normal;
  cursor: pointer;
  border: 1px solid #335080;
}

::v-deep .rounded-pill {
  border-radius: 20px !important;
}

::v-deep .pill-tabs {
  border: 1px solid #d4e0ed;
  border-radius: 30px;
}

.min-height {
  min-height: 300px;
}
::v-deep .min-height-modal {
  min-height: 500px !important;
}

.right-button {
  right: 0;
  top: 2px;
}
</style>
